import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Bot } from 'lucide-react';
import api from '../lib/api';
import { cn, formatDate } from '../lib/utils';
import { useUIStore } from '../stores/uiStore';

export default function AiCreditsBadge({ collapsed = false }) {
  const { t } = useTranslation();
  const direction = useUIStore((s) => s.direction);
  const locale = direction === 'rtl' ? 'ar-JO' : 'en-JO';
  const [credits, setCredits] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    api.get('/ai/credits')
      .then((res) => setCredits(res?.data))
      .catch(() => {});
  }, []);

  if (!credits || credits.credits_limit === 0) return null;

  const remaining = credits.credits_remaining;
  const limit = credits.credits_limit;
  const pct = limit > 0 ? (remaining / limit) * 100 : 0;

  const colorClass = pct > 50
    ? 'text-emerald-600 bg-emerald-50'
    : pct > 20
      ? 'text-amber-600 bg-amber-50'
      : 'text-red-600 bg-red-50';

  if (collapsed) {
    return (
      <div className="flex justify-center px-2 py-1.5">
        <div
          className={cn('w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold', colorClass)}
          title={t('ai.creditsRemaining')}
        >
          {remaining}
        </div>
      </div>
    );
  }

  return (
    <div className="relative px-3 py-2">
      <button
        type="button"
        onClick={() => setShowTooltip((s) => !s)}
        onBlur={() => setTimeout(() => setShowTooltip(false), 150)}
        className={cn(
          'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors',
          colorClass
        )}
      >
        <Bot size={14} />
        <span className="flex-1 text-start">
          {remaining === 0
            ? t('ai.creditsExhausted')
            : `${remaining} / ${limit}`
          }
        </span>
      </button>

      {showTooltip && (
        <div className="absolute bottom-full mb-2 start-3 end-3 z-50 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg">
          <p className="font-medium mb-1">{t('ai.creditsRemaining')}</p>
          {remaining === 0 && credits.resets_at && (
            <p className="text-gray-400">
              {t('ai.creditsRenewAt', { date: formatDate(credits.resets_at, locale) })}
            </p>
          )}
          {/* Progress bar */}
          <div className="mt-2 h-1.5 bg-gray-700 rounded-full overflow-hidden">
            <div
              className={cn(
                'h-full rounded-full transition-all',
                pct > 50 ? 'bg-emerald-500' : pct > 20 ? 'bg-amber-500' : 'bg-red-500'
              )}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
